// SPDX-License-Identifier: AGPL-3.0-or-later

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from './client.js';
import type { MeResponse, PublicUser } from './types.js';

export const meQueryKey = ['auth', 'me'] as const;

export function useMe() {
  return useQuery<MeResponse | null>({
    queryKey: meQueryKey,
    queryFn: async () => {
      try {
        return await apiFetch<MeResponse>('/api/auth/me');
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
  });
}

export interface AuthProvidersResponse {
  providers: {
    password: boolean;
    astrumSso: boolean;
  };
}

export const authProvidersQueryKey = ['auth', 'providers'] as const;

/**
 * Which sign-in methods the API offers (200.3). Drives whether the login page
 * renders the Astrum SSO button. Failures degrade to password-only rather
 * than blocking sign-in — an unreachable providers endpoint must never take
 * the login form down with it.
 */
export function useAuthProviders() {
  return useQuery<AuthProvidersResponse['providers']>({
    queryKey: authProvidersQueryKey,
    queryFn: async () => {
      try {
        const res = await apiFetch<AuthProvidersResponse>('/api/auth/providers');
        return res.providers;
      } catch {
        return { password: true, astrumSso: false };
      }
    },
    staleTime: 5 * 60_000,
  });
}

export interface LoginInput {
  email: string;
  password: string;
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation<{ user: PublicUser }, ApiError, LoginInput>({
    mutationFn: (body) => apiFetch('/api/auth/login', { method: 'POST', body }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: meQueryKey });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, undefined>({
    mutationFn: () => apiFetch('/api/auth/logout', { method: 'POST', body: {} }),
    onSuccess: async () => {
      qc.setQueryData<MeResponse | null>(meQueryKey, null);
      await qc.invalidateQueries();
    },
  });
}

/** Minimum new-password length (mirrors the API's NewPasswordSchema, 8–256). */
export const MIN_PASSWORD_LENGTH = 8;

export interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
}

/**
 * Self-service password change (requireAuth). The API revokes other sessions
 * on success, so we clear cached queries afterwards.
 */
export function useChangePassword() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, ChangePasswordInput>({
    mutationFn: (body) => apiFetch('/api/auth/change-password', { method: 'POST', body }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: meQueryKey });
    },
  });
}

/**
 * Request a password-reset email. Always resolves for a well-formed request
 * (the API does not reveal whether the email exists), so the UI shows the same
 * confirmation regardless.
 */
export function useForgotPassword() {
  return useMutation<{ ok: true }, ApiError, { email: string }>({
    mutationFn: (body) => apiFetch('/api/auth/forgot-password', { method: 'POST', body }),
  });
}

export interface ResetPasswordInput {
  token: string;
  newPassword: string;
}

/** Complete a reset using the token from the emailed link. */
export function useResetPassword() {
  return useMutation<{ ok: true }, ApiError, ResetPasswordInput>({
    mutationFn: (body) => apiFetch('/api/auth/reset-password', { method: 'POST', body }),
  });
}
